import React, { useState } from 'react';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Glyphicon, OverlayTrigger, Popover, } from 'react-bootstrap';
import { titleize, unescapeHTML } from 'underscore.string';
import { Link } from 'react-router-dom';
import CustomersAPIUtils from 'utils/CustomersAPIUtils';
import SessionStore from 'stores/SessionStore';
import ToastStore from 'stores/ToastStore';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import CargomaticCard from 'components/v2/card';
import Badge from 'components/v2/Badge';
import EditButton from 'components/v2/buttons/EditButton';
import LabelValue from 'components/ui/tailwind/label-value';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import { getAddressString, hasAddress, } from 'utils/transformers/AddressTransformer';
import CargomaticModal from 'components/v2/modal';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { createPhoneNumberWithExtension } from 'utils/formatters/Phone';
import Sidebar from '../../carrier/AdminSidebar';
import { Avatar } from '../Avatar';
import Capability from '../Capability';
import CarrierForm from './carrier-form';
import AccountContacts from '../../../v2/contacts';
import CarrierAccountAccess from './CarrierAccountAccess';
const STATUSES = SessionStore.hasComplianceRole()
    ? {
        AWAITING_TRAINING: 'Awaiting Training',
        TRAINED: 'Trained',
        BROKER: 'Broker',
    }
    : {
        AWAITING_TRAINING: 'Awaiting Training',
        TRAINED: 'Trained',
    };
const STATUS_OPTIONS = SessionStore.hasComplianceRole()
    ? ['Trained', 'MVP', 'Broker']
    : ['Trained', 'MVP'];
const loginAs = async (grantorId) => {
    const admin = SessionStore.getUser();
    try {
        const user = await UserWebAPIUtils.loginAs(admin._id, grantorId).catch((err) => {
            ToastStore.getNotifications().push({
                title: 'Error',
                body: err,
                level: 'error',
            });
        });
        if (user) {
            window.location.href = '/';
        }
    }
    catch (err) {
        ToastStore.getNotifications().push({
            title: 'Error',
            body: err,
            level: 'error',
        });
    }
};
const renderAddress = (address) => {
    const addressString = getAddressString(address);
    return React.createElement(LabelValue, { value: addressString, icon: "map-marker" });
};
const handleStatusUpdates = (type, value, _id) => {
    let update = { _id };
    switch (type) {
        case 'Trained':
            update.status = value ? STATUSES.TRAINED : STATUSES.AWAITING_TRAINING;
            break;
        case 'MVP':
            update.mvp = value;
            break;
        case 'Broker':
            update.is_broker = value;
            break;
        default:
    }
    CustomersAPIUtils.updateCarrier(update);
};
const CarrierAccountDetails = (props) => {
    const { t } = useTranslation();
    const { user: { name, _id, first_name, last_name, phone, phone_extension, email, address, is_broker, authorized: authorization, has_flatbed_trucks, hazmat_certified, tsa_certified, is_owner_operator, is_dray, test, enterprise, preferred_carrier, status, mvp, bank_verified, subsidiary, driver_categorization, lease_on, }, verification: { isVerified, authorized, bankTransfer, documentAlert }, onOpenJobs, onOpenPassword, onOpenAccountRequest, handleStatusUpdates, hasLoginAsPermission, } = props;
    const checkStatus = (type) => {
        switch (type) {
            case 'Trained':
                return status === STATUSES.TRAINED;
            case 'MVP':
                return mvp;
            case 'Broker':
                return is_broker;
            default:
        }
    };
    const renderStatus = () => {
        return (React.createElement("div", { className: "dropdown-filter-wrapper mb-4 flex justify-center" },
            React.createElement(OverlayTrigger, { className: "dropdown-filter-wrapper", rootClose: true, trigger: "click", placement: "bottom", overlay: renderStatusPopup() },
                React.createElement("div", null,
                    React.createElement(Button, { style: { outline: 'none' }, bsSize: "small" },
                        React.createElement("strong", null, t('STATUS')),
                        React.createElement(Glyphicon, { glyph: "chevron-down", className: "button-down-glyph" }))))));
    };
    const renderStatusPopup = () => {
        const isSupport = SessionStore.isSupport();
        return (React.createElement(Popover, { id: "carrier-status-popup" },
            React.createElement("ul", { className: "filter-dropdown-wrapper" }, STATUS_OPTIONS.map((type) => {
                return (React.createElement("li", { key: type },
                    React.createElement(Checkbox, { onChange: (e) => handleStatusUpdates(type, e.target.checked, _id), checked: checkStatus(type), disabled: type === 'Trained' && !isSupport }, t(type.toUpperCase()))));
            }))));
    };
    return (React.createElement("div", { className: "" },
        React.createElement(CargomaticCard, { className: "mb-6" },
            React.createElement("div", { className: "flex justify-center mb-2" },
                React.createElement(Avatar, { key: _id, userId: _id, className: "h-32 w-32 cursor-pointer rounded-full" })),
            React.createElement("div", { className: "relative w-min my-0 mx-auto" },
                React.createElement("h3", { className: "my-2 text-xl text-center text-grey-darker whitespace-nowrap" }, `${unescapeHTML(name)}`),
                mvp ? (React.createElement("img", { src: "/img/cargomatic-circle.png", className: "absolute -top-1 -right-4 w-4", alt: "MVP" })) : null),
            React.createElement("div", { className: "flex flex-wrap justify-center -mx-2" },
                React.createElement("div", { className: "flex-none px-2 mb-2" },
                    React.createElement(Badge, { color: "blue", size: "large", rounded: true }, titleize(t('CARRIER', { count: 1 })))),
                React.createElement("div", { className: "flex-none px-2 mb-2" },
                    React.createElement(Badge, { color: authorized === 'authorized' ? 'brand' : 'red', size: "large", rounded: true }, t(authorized.toUpperCase()))),
                is_broker && (React.createElement("div", { className: "flex-none px-2 mb-2" },
                    React.createElement(Badge, { color: "orange", size: "large", rounded: true }, t('BROKER')))),
                React.createElement("div", { className: "flex-none px-2 mb-2" },
                    React.createElement(Badge, { color: isVerified === 'verified' ? 'brand' : 'red', size: "large", rounded: true }, t('COMPANY_REPRESENTATIVE', {
                        isVerified: t(isVerified.toUpperCase()),
                    }))),
                React.createElement("div", { className: "flex-none px-2 mb-2" },
                    React.createElement(Badge, { color: bankTransfer === 'enabled' ? 'brand' : 'red', size: "large", rounded: true }, t('BANK_TRANSFERS', {
                        bankTransfer: t(bankTransfer.toUpperCase()),
                    }))),
                isVerified === ' unverified' && (React.createElement("div", { className: "flex-none px-2 mb-2" },
                    React.createElement(Badge, { color: "red", size: "large", rounded: true }, documentAlert)))),
            React.createElement("div", { className: "flex justify-center mt-4" },
                React.createElement("button", { className: "button text-grey-darker px-2", onClick: onOpenJobs }, t('VIEW_JOBS')),
                React.createElement(Link, { className: "button text-grey-darker no-underline px-2", to: `/carrier-metrics/${_id}` }, t('VIEW_SCORECARD')),
                SessionStore.isSupport() && (React.createElement("button", { className: "button text-grey-darker px-2", onClick: () => loginAs(_id) }, t('LOG_IN_AS')))),
            renderStatus()),
        React.createElement(CargomaticCard, { className: "mb-6", header: () => (React.createElement("header", { onClick: () => props.onOpenModal('account'), className: "flex flex-wrap items-center justify-between" },
                React.createElement("h3", { className: "my-0" }, t('ACCOUNT')),
                React.createElement(EditButton, null))) },
            React.createElement(LabelValue, { value: `${unescapeSpecialChars(name)}`, icon: "home" }),
            React.createElement(LabelValue, { value: `${unescapeSpecialChars(first_name)} ${unescapeSpecialChars(last_name)}`, icon: "user" }),
            React.createElement(LabelValue, { value: createPhoneNumberWithExtension(phone, phone_extension, t('EXT')), icon: "phone" }),
            React.createElement(LabelValue, { value: `${email}`, icon: "envelope" }),
            React.createElement("hr", null),
            React.createElement("div", { className: "" },
                React.createElement("header", { onClick: () => props.onOpenModal('address'), className: "flex flex-wrap justify-between" },
                    React.createElement("h3", { className: "my-0" }, t('ADDRESS')),
                    React.createElement(EditButton, null)),
                React.createElement("div", { className: "mb-6" },
                    hasAddress(address) && renderAddress(address),
                    !hasAddress(address) && (React.createElement(LabelValue, { value: t('NO_ADDRESS_ON_FILE'), icon: "map-marker" }))))),
        React.createElement(CargomaticCard, { className: "mb-6", header: () => (React.createElement("header", { onClick: () => props.onOpenModal('capabilities'), className: "flex flex-wrap justify-between" },
                React.createElement("h3", { className: "my-0" }, t('CAPABILITIES')),
                React.createElement(EditButton, null))) },
            React.createElement(AuthorizationWrapper, { excludedRegions: [REGIONS.US] },
                React.createElement(Capability, { label: t('BANK_VERIFIED'), checked: bank_verified })),
            React.createElement(Capability, { label: t('AUTHORIZED'), checked: authorization }),
            React.createElement(Capability, { label: t('ENTERPRISE_CARRIER'), checked: enterprise }),
            React.createElement(Capability, { label: t('PREFERRED_CARRIER'), checked: preferred_carrier }),
            React.createElement(Capability, { label: `${t('HAS_FLATBED_TRUCKS')} ?`, checked: has_flatbed_trucks }),
            React.createElement(Capability, { label: t('HAZMAT_CERTIFIED'), checked: hazmat_certified }),
            React.createElement(Capability, { label: t('TSA_CERTIFIED'), checked: tsa_certified }),
            React.createElement(Capability, { label: t('OWNER_OPERATOR'), checked: is_owner_operator }),
            React.createElement(Capability, { label: t('DRAY_OPERATOR'), checked: is_dray }),
            React.createElement(Capability, { label: t('TEST'), checked: test }),
            React.createElement(Capability, { label: t('LEASE_ON'), checked: lease_on }),
            React.createElement("hr", null),
            React.createElement("div", { className: "w-full flex justify-between py-2" },
                React.createElement("label", { className: "form-label" }, "Subsidiary"),
                React.createElement("p", null, subsidiary ? t(subsidiary.toUpperCase()) : 'Not Set')),
            React.createElement("div", { className: "w-full flex justify-between py-2" },
                React.createElement("label", { className: "form-label" }, t('DRIVER_CATEGORIZATION')),
                React.createElement("p", null, driver_categorization
                    ? t(driver_categorization.toUpperCase())
                    : 'Not Set'))),
        React.createElement("div", { className: "my-6" },
            React.createElement("button", { onClick: () => onOpenPassword(), className: "w-full button button-hollow-red" }, t('CHANGE_PASSWORD'))),
        SessionStore.isSupport() && !hasLoginAsPermission && (React.createElement("div", { className: "my-6" },
            React.createElement("button", { onClick: () => onOpenAccountRequest(), className: "w-full button button-green", disabled: !SessionStore.isSupport() }, t('REQUEST_PERMISSION_TO_LOGIN_AS'))))));
};
const hasLegalEntityDocumentDue = (fieldsNeeded) => {
    return fieldsNeeded.indexOf('legal_entity.verification.document') >= -1;
};
const hasDocumentDue = (requirements) => {
    const verificationFields = ['currently_due', 'eventually_due', 'past_due'];
    const verificationDoc = 'verification.document';
    const missingVerificationDocs = verificationFields.filter((field) => requirements[field] && requirements[field].indexOf(verificationDoc) > -1);
    return missingVerificationDocs.length > 0;
};
const AdminCarrierDetails = ({ carrier, onOpenJobs, onOpenPassword, onOpenAccountRequest, t, onSaveDetails, }) => {
    const [showModal, setShowModal] = useState(false);
    const renderModalComponent = (category) => {
        return (React.createElement(CarrierForm, { category: category, details: carrier, onSave: (data) => onSaveDetails('Carrier', data) }));
    };
    const renderSidebar = () => (React.createElement(Sidebar, { userId: carrier._id, activePage: "profile" }));
    const breadcrumbs = {
        pages: [
            {
                name: (carrier === null || carrier === void 0 ? void 0 : carrier.name) || t('CARRIER'),
                href: `/users/${carrier._id}`,
                current: true,
            },
        ],
    };
    let authorized = 'unauthorized';
    let is_broker = !!carrier.is_broker;
    if (carrier.authorized)
        authorized = 'authorized';
    let isVerified = 'unverified';
    let bankTransfer = 'disabled';
    let fieldsNeeded = 'no verification fields are needed';
    let requirements = {};
    let documentAlert = '';
    if ((isVerified === 'unverified' &&
        fieldsNeeded &&
        hasLegalEntityDocumentDue(fieldsNeeded)) ||
        (requirements && hasDocumentDue(requirements))) {
        documentAlert = 'Needs DL or Passport Document';
    }
    return (React.createElement(LayoutWithSidebar, { sidebar: renderSidebar, breadcrumbs: breadcrumbs, className: "py-4" },
        React.createElement("div", { className: "lg:grid lg:grid-cols-2 lg:gap-4" },
            React.createElement(CarrierAccountDetails, { user: carrier, verification: {
                    authorized,
                    isVerified,
                    bankTransfer,
                    documentAlert,
                }, onOpenJobs: onOpenJobs, onOpenPassword: onOpenPassword, onOpenAccountRequest: onOpenAccountRequest, onOpenModal: (category) => setShowModal(category), handleStatusUpdates: handleStatusUpdates, hasLoginAsPermission: false }),
            React.createElement("div", null,
                React.createElement(AccountContacts, { className: "mb-6", contacts: carrier.contacts, userId: carrier._id }),
                React.createElement(CargomaticCard, { header: () => React.createElement("h3", { className: "my-0" }, t('PARTNER_ACCESS')), grayFooter: true },
                    React.createElement(CarrierAccountAccess, { carrier: carrier, perPage: 5 })))),
        showModal && (React.createElement(CargomaticModal, { show: true, onHide: () => setShowModal(false), title: t('EDIT_ACCOUNT'), body: renderModalComponent(showModal), footer: null, location: "middle" }))));
};
export default AdminCarrierDetails;
